<template>
  <fragment>
    <b-form @submit.prevent="onUpdate">
      <b-form-row>
        <b-col cols="12" xl="4">
          <h2>Email Settings</h2>
        </b-col>
        <b-col cols="12" md="6" xl="4" class="mt-3 mt-xl-1">
          <b-form-row>
            <b-col cols="5" class="d-flex justify-content-between">
              <div class="d-flex flex-column align-items-center">
                <span>Include</span>
                <span>Opt In/Out</span>
              </div>
              <b-form-checkbox
                  inline
                  v-model="includeOptOut"
                  @change="$v.isFieldSelected.$touch"
              />
            </b-col>
            <b-col cols="7">
              <b-form-radio-group
                  v-model="isOptOut"
                  :options="[
                  { text: 'Opt In', value: 0 },
                  { text: 'Opt Out', value: 1 },
                ]"
                  :disabled="!includeOptOut"
              />
            </b-col>
          </b-form-row>
        </b-col>
        <b-col cols="12" md="6" xl="4" class="mt-3 mt-xl-1">
          <b-form-row>
            <b-col cols="5" class="d-flex justify-content-between">
              <div class="d-flex flex-column align-items-center">
                <span>Include</span>
                <span>Valid/Invalid</span>
              </div>
              <b-form-checkbox
                  inline
                  v-model="includeInvalid"
                  @change="$v.isFieldSelected.$touch()"
              />
            </b-col>
            <b-col cols="7">
              <b-form-radio-group
                  v-model="isInvalid"
                  :options="[
                  { text: 'Valid', value: 0 },
                  { text: 'Invalid', value: 1 },
                ]"
                  :disabled="!includeInvalid"
              />
            </b-col>
          </b-form-row>
        </b-col>
      </b-form-row>
      <b-form-row v-show="$v.isFieldSelected.$anyError" class="mt-2">
        <b-col
            cols="12"
            xl="8"
            offset-xl="4"
            class="v2_color_red v2_font_size_12"
        >
          At least one of the checkboxes needs to be selected
        </b-col>
      </b-form-row>
      <b-form-row class="mt-4">
        <b-col cols="12">
          <b-form-group
              label-class="font-weight-bold"
              description="Emails should be comma separated. For example, example1@example1.com,example2@example2.com"
              invalid-feedback="Wrong input format, please check the hint"
          >
            <b-form-textarea
                rows="6"
                placeholder="Emails"
                v-model.trim="$v.emails.$model"
                :state="validateState('emails')"
            />
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row>
        <b-col cols="12" class="text-right">
          <b-button type="submit" variant="primary">
            Update
          </b-button>
        </b-col>
      </b-form-row>
    </b-form>
    <v-wait for="updateLoading">
      <template slot="waiting">
        <half-circle-spinner/>
      </template>
      <template>
        <b-row class="mt-3">
          <b-col cols="12">
            <b-alert variant="success" :show="updated > 0">
              {{ updated }} user(s) updated successfully
            </b-alert>
          </b-col>
          <b-col cols="12" v-for="(error, indx) in errors" :key="indx">
            <b-alert variant="danger" show>
              {{ error }}
            </b-alert>
          </b-col>
        </b-row>
      </template>
    </v-wait>
  </fragment>
</template>

<script>
import {validationMixin} from 'vuelidate';
import {helpers, required} from 'vuelidate/lib/validators';
import validateStateMixin from '@/mixins/validateStateMixin';
import {HalfCircleSpinner} from 'epic-spinners';
import {handleServerErrorWithValidation} from '@/helpers/ToastedHelper';
import Api from '@/providers/Api';
import Help from "@/providers/Help";

const commaSeparatedEmails = helpers.regex(
    'commaSeparatedEmails',
    /^([^\s,]+@[^\s,]+\.[^\s,]+\s?,)*([^\s,]+@[^\s,]+\.[^\s,]+)$/
);

export default {
  name: 'EmailBulkSettings',
  mixins: [validationMixin, validateStateMixin],
  components: {
    HalfCircleSpinner,
  },
  data: function () {
    return {
      includeInvalid: false,
      includeOptOut: false,
      isInvalid: 0,
      isOptOut: 0,
      emails: '',
      updated: 0,
      errors: [],
    };
  },
  validations: {
    emails: {
      commaSeparatedEmails,
      required,
    },
    isFieldSelected: {
      oneSelected: (_, vm) => vm.includeInvalid || vm.includeOptOut,
    },
  },
  methods: {
    update: async function () {
      this.$v.$touch();
      if (this.$v.$anyError) {
        return;
      }
      this.$wait.start('updateLoading');
      this.updated = 0;
      this.errors = [];
      try {
        const updateData = {
          emails: this.emails.split(','),
        };
        if (this.includeOptOut) {
          updateData.is_optout = this.isOptOut;
        }
        if (this.includeInvalid) {
          updateData.is_invalid = this.isInvalid;
        }
        const {updated, errors} = await Api.emailSettingsBulkUpdate(
            updateData
        );
        this.updated = updated;
        this.errors = errors;
      } catch (e) {
        handleServerErrorWithValidation(e, this.$toasted);
      } finally {
        this.$wait.end('updateLoading');
      }
    },
    onUpdate: function () {
      Help.confirm.show('Are you sure you want to set these values for the e-mail list?', 'Yes', () => {
        this.update();
      });
    }
  },
};
</script>

<style scoped></style>
